import React from 'react';
import { Link } from 'react-router-dom';

import PropTypes from 'prop-types';

import Card from '@/components/common/Card';
import permissionsImage from '@/components/common/icons/document-circle.svg';
import editImage from '@/components/common/icons/edit.svg';
import detailsImage from '@/components/common/icons/inspector-details.svg';
import trashImage from '@/components/common/icons/trash.svg';
import userImage from '@/components/common/icons/user-circle.svg';
import Spinner from '@/components/common/Spinner';
import urlPrefix from '@/helpers/url-prefix';

const SectionTitle = ({ icon, children, isFirst }) => (
  <h3
    className={`flex items-center h-[24px] mb-0 !text-[13.14px] !font-bold !leading-[1.1] text-[#393939] ${
      isFirst ? 'mt-2' : '!mt-[19.71px]'
    }`}
  >
    <div className="w-[45.47px] flex items-center justify-center flex-shrink-0">
      {typeof icon === 'string' ? (
        <img src={icon} alt="" className="h-[24px] w-auto" />
      ) : (
        React.cloneElement(icon, { className: 'h-[24px] w-auto' })
      )}
    </div>
    <span className="text-[13.14px] font-bold leading-[1.1] text-[#393939]">{children}</span>
  </h3>
);

SectionTitle.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  isFirst: PropTypes.bool,
};

SectionTitle.defaultProps = {
  isFirst: false,
};

const FieldRow = ({ children }) => (
  <div className="flex justify-between mt-[1.25em] px-[3.46em] first:mt-0">{children}</div>
);

FieldRow.propTypes = {
  children: PropTypes.node.isRequired,
};

const FieldCol = ({ label, value }) => (
  <div className="flex flex-col w-[calc(50%-5px)]">
    <p className="m-0 text-[0.75em] font-bold uppercase text-[#393939]">{label}</p>
    <p
      className="text-[0.875em] font-semibold text-[#919191] mt-[0.4em] mb-0 leading-[1.4] overflow-hidden text-ellipsis whitespace-nowrap"
      title={value || ''}
    >
      {value || '-----'}
    </p>
  </div>
);

FieldCol.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

FieldCol.defaultProps = {
  value: null,
};

const UserDetailsComponent = ({
  id,
  isFeaturesBusy,
  isUserBusy,
  isDeleteConfirmationVisible,
  intl,
  features,
  user,
  cancelDelete,
  handleDelete,
  showDeleteConfirmation,
}) => {
  if (isUserBusy) {
    return (
      <div className="flex flex-col p-[1.5em_1em]">
        <div className="flex justify-center items-center py-[1.5em]">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-[1.5em_1em]">
      {user ? (
        <>
          <Card className="fade-in mb-[0.3em]">
            <div className="h-[32px]">
              {!isDeleteConfirmationVisible && (
                <div className="flex justify-center m-0 -mx-[5px]">
                  <Link
                    to={`${urlPrefix('users')}/${id}/edit`}
                    data-tip={intl.formatMessage({ id: 'USER_EDIT' })}
                    className="inline-flex justify-center items-center w-[64px] h-[32px] mx-[5px] px-0 border border-[#e3e3e3] rounded-[3px] bg-white font-bold text-[0.9375em] leading-[1.4] text-[#393939] transition-all hover:bg-[#f5f5f5] focus:outline-none"
                  >
                    <img src={editImage} alt={intl.formatMessage({ id: 'EDIT' })} className="h-[18px]" />
                  </Link>
                  <button
                    type="button"
                    data-tip={intl.formatMessage({ id: 'USER_DELETE' })}
                    onClick={showDeleteConfirmation}
                    className="inline-flex justify-center items-center w-[64px] h-[32px] mx-[5px] px-0 border border-[#e3e3e3] rounded-[3px] bg-white font-bold text-[0.9375em] leading-[1.4] text-[#393939] transition-all hover:bg-[#f5f5f5] focus:outline-none"
                  >
                    <img src={trashImage} alt={intl.formatMessage({ id: 'INSPECTOR_DELETE' })} className="h-[18px]" />
                  </button>
                </div>
              )}
              {isDeleteConfirmationVisible && (
                <div className="flex justify-between items-center text-[0.875em] font-bold px-[0.5rem] w-full">
                  <div className="leading-none text-[#393939]">
                    {intl.formatMessage({ id: 'DELETE_USER_MESSAGE' })}?
                  </div>
                  <div className="flex m-0 -mx-[5px] !gap-2">
                    <button
                      type="button"
                      onClick={cancelDelete}
                      className="inline-flex justify-center items-center w-[64px] h-[32px] mx-[5px] px-0 border border-[#e3e3e3] rounded-[3px] bg-white font-bold text-[0.9375em] leading-[1.4] text-[#393939] transition-all hover:bg-[#f5f5f5] focus:outline-none"
                    >
                      {intl.formatMessage({ id: 'NO' })}
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex justify-center items-center w-[64px] h-[32px] mx-[5px] px-0 border border-[#e3e3e3] rounded-[3px] bg-white font-bold text-[0.9375em] leading-[1.4] text-[#393939] transition-all hover:bg-[#f5f5f5] focus:outline-none"
                    >
                      {intl.formatMessage({ id: 'YES' })}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="fade-in">
            <h2 className="flex items-center h-[53.5px] mb-[24.5px] pb-[24.5px] border-b border-[#eee] !text-[17.5px] !font-bold !leading-[22.75px] text-[#393939]">
              <div className="w-[28px] h-[28px] flex items-center justify-center flex-shrink-0 mr-[8.75px]">
                <img src={detailsImage} alt={intl.formatMessage({ id: 'DETAILS' })} className="max-w-[28px] h-[28px]" />
              </div>
              <span className="text-[17.5px] font-bold leading-[22.75px] text-[#393939]">
                {intl.formatMessage({ id: 'DETAILS' })}
              </span>
            </h2>

            <SectionTitle icon={userImage} isFirst>
              {intl.formatMessage({ id: 'BASIC_DATA' })}
            </SectionTitle>

            <FieldRow>
              <FieldCol label={intl.formatMessage({ id: 'fullName' })} value={user?.name} />
              <FieldCol label={intl.formatMessage({ id: 'CPF' })} value={user?.cpf} />
            </FieldRow>

            <FieldRow>
              <FieldCol label={intl.formatMessage({ id: 'USER' })} value={user?.username} />
              <FieldCol label={intl.formatMessage({ id: 'EMAIL' })} value={user?.email} />
            </FieldRow>

            <FieldRow>
              <FieldCol label={intl.formatMessage({ id: 'CONTACT' })} value={user?.phone} />
              <FieldCol label={intl.formatMessage({ id: 'CREATED_AT' })} value={user?.created_at} />
            </FieldRow>

            <SectionTitle icon={permissionsImage}>{intl.formatMessage({ id: 'PERMISSIONS' })}</SectionTitle>

            <div className="px-[45.47px] mt-[16.43px]">
              {isFeaturesBusy ? (
                <div className="flex justify-center items-center py-[1.5em]">
                  <Spinner />
                </div>
              ) : (
                <ul className="list-none flex justify-between flex-wrap m-0 p-0">
                  {features.map(feature => (
                    <li
                      key={feature?.description}
                      className="flex items-center w-[calc(50%-5px)] h-[24px] leading-none break-all"
                      title={intl.formatMessage({ id: `PERMISSION.${feature?.description}` })}
                    >
                      <label className="flex items-center m-0 whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer text-[9.86px] font-bold uppercase text-[#393939]">
                        <input
                          type="checkbox"
                          checked={feature?.selected}
                          readOnly
                          className="mr-[6px] align-middle flex-shrink-0 cursor-default"
                        />
                        <span className="ml-[4px]">
                          {intl.formatMessage({ id: `PERMISSION.${feature?.description}` })}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </>
      ) : (
        <Card className="fade-in">
          <div className="text-center">
            <h5 className="flex items-center mt-[2rem] mb-[1rem] text-[0.94em] font-extrabold text-[#393939] pl-[2rem] pr-[2rem]">
              {intl.formatMessage({ id: 'NOT_FOUND_MESSAGE' })}
            </h5>
          </div>
        </Card>
      )}
    </div>
  );
};

UserDetailsComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isFeaturesBusy: PropTypes.bool.isRequired,
  isUserBusy: PropTypes.bool.isRequired,
  isDeleteConfirmationVisible: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  features: PropTypes.array,
  user: PropTypes.object,
  cancelDelete: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  showDeleteConfirmation: PropTypes.func.isRequired,
};

UserDetailsComponent.defaultProps = {
  features: null,
  user: null,
};

export default UserDetailsComponent;
